import React, { useEffect, useState, useRef, useCallback } from 'react';
import { clickable } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

function fmtDate(s) { return s || ''; }

/* ── Toolbar button ── */
function ToolBtn({ title, onClick, children }) {
  return (
    <button title={title} onMouseDown={e => { e.preventDefault(); onClick(); }}
      style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer',
        background: 'var(--surface)', color: 'var(--text)',
        fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </button>
  );
}

/* ── Rich text editor ── */
function RichEditor({ value, onChange }) {
  const ref = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
  }, []);

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(ref.current?.innerHTML || '');
  };

  const insertFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      ref.current?.focus();
      if (file.type.startsWith('image/')) {
        document.execCommand('insertImage', false, ev.target.result);
      } else {
        const html = `<a href="${ev.target.result}" download="${file.name}" style="display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:6px;border:1px solid #cbd5e1;background:#f8fafc;color:#334155;font-size:13px;text-decoration:none;font-family:inherit;">📎 ${file.name}</a>`;
        document.execCommand('insertHTML', false, html);
      }
      onChange(ref.current?.innerHTML || '');
    };
    reader.readAsDataURL(file);
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        insertFile(item.getAsFile());
        return;
      }
    }
  }, [insertFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    Array.from(e.dataTransfer.files || []).forEach(insertFile);
  }, [insertFile]);

  const handleAnyFile = (e) => {
    Array.from(e.target.files || []).forEach(insertFile);
    e.target.value = '';
  };

  const fileImgRef = useRef(null);
  const fileAnyRef = useRef(null);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'flex', gap: 4, padding: '6px 10px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <ToolBtn title="Bold" onClick={() => exec('bold')}><b>B</b></ToolBtn>
        <ToolBtn title="Italic" onClick={() => exec('italic')}><i>I</i></ToolBtn>
        <ToolBtn title="Underline" onClick={() => exec('underline')}><u>U</u></ToolBtn>
        <ToolBtn title="Strike" onClick={() => exec('strikeThrough')}><s>S</s></ToolBtn>
        <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
        <ToolBtn title="Heading 1" onClick={() => exec('formatBlock', 'h1')}>H1</ToolBtn>
        <ToolBtn title="Heading 2" onClick={() => exec('formatBlock', 'h2')}>H2</ToolBtn>
        <ToolBtn title="Heading 3" onClick={() => exec('formatBlock', 'h3')}>H3</ToolBtn>
        <ToolBtn title="Paragraph" onClick={() => exec('formatBlock', 'p')}>P</ToolBtn>
        <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
        <ToolBtn title="Bullet list" onClick={() => exec('insertUnorderedList')}>• List</ToolBtn>
        <ToolBtn title="Numbered list" onClick={() => exec('insertOrderedList')}>1. List</ToolBtn>
        <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
        <ToolBtn title="Horizontal rule" onClick={() => exec('insertHorizontalRule')}>─</ToolBtn>
        <ToolBtn title="Remove formatting" onClick={() => exec('removeFormat')}>Tx</ToolBtn>
        <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
        <ToolBtn title="Insert image" onClick={() => fileImgRef.current?.click()}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          &nbsp;Image
        </ToolBtn>
        <ToolBtn title="Attach file" onClick={() => fileAnyRef.current?.click()}>📎&nbsp;Attach</ToolBtn>
        <input ref={fileImgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAnyFile} />
        <input ref={fileAnyRef} type="file" accept="*/*" multiple style={{ display: 'none' }} onChange={handleAnyFile} />
        <span style={{ fontSize: 11, color: 'var(--text-2)', alignSelf: 'center', marginLeft: 4 }}>paste or drag & drop files</span>
      </div>
      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); }}
        onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
        onDrop={handleDrop}>
        {dragOver && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'var(--accent-light)',
            border: '2px dashed var(--accent)', borderRadius: 4, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
            <svg width="32" height="32" fill="none" stroke="var(--accent)" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>Drop to attach</div>
            <div style={{ fontSize: 11, color: 'var(--accent)', opacity: .7 }}>Images embed inline · other files as download links</div>
          </div>
        )}
        <div ref={ref} contentEditable suppressContentEditableWarning
          onInput={() => onChange(ref.current?.innerHTML || '')}
          onPaste={handlePaste}
          style={{ flex: 1, minHeight: 360, padding: '16px 20px', outline: 'none', overflowY: 'auto',
            fontSize: 14, lineHeight: 1.7, color: 'var(--text)' }}
        />
      </div>
      <style>{`
        [contenteditable] h1 { font-size: 22px; font-weight: 700; margin: 16px 0 8px; }
        [contenteditable] h2 { font-size: 18px; font-weight: 700; margin: 14px 0 6px; }
        [contenteditable] h3 { font-size: 15px; font-weight: 700; margin: 12px 0 4px; }
        [contenteditable] img { max-width: 100%; border-radius: 6px; margin: 8px 0; display: block; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 24px; margin: 6px 0; }
        [contenteditable] li { margin: 3px 0; }
        [contenteditable] hr { border: none; border-top: 1px solid var(--border); margin: 12px 0; }
        [contenteditable]:empty:before { content: 'Start writing… paste or drag & drop files here'; color: var(--text-2); }
      `}</style>
    </div>
  );
}

/* ── Article view (read mode) ── */
function ArticleView({ article }) {
  return (
    <div style={{ padding: '0 4px' }}>
      <style>{`
        .wiki-body h1 { font-size: 22px; font-weight: 700; margin: 16px 0 8px; color: var(--text); }
        .wiki-body h2 { font-size: 18px; font-weight: 700; margin: 14px 0 6px; color: var(--text); border-bottom: 1px solid var(--border); padding-bottom: 4px; }
        .wiki-body h3 { font-size: 15px; font-weight: 700; margin: 12px 0 4px; color: var(--text); }
        .wiki-body p  { margin: 6px 0; line-height: 1.7; }
        .wiki-body img { max-width: 100%; border-radius: 8px; margin: 10px 0; display: block; box-shadow: 0 2px 12px rgba(0,0,0,.12); cursor: pointer; }
        .wiki-body ul, .wiki-body ol { padding-left: 24px; margin: 6px 0; }
        .wiki-body li { margin: 3px 0; line-height: 1.6; }
        .wiki-body hr { border: none; border-top: 1px solid var(--border); margin: 16px 0; }
        .wiki-body b, .wiki-body strong { font-weight: 700; }
      `}</style>
      <div className="wiki-body" style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: article.content }}
        onClick={e => {
          if (e.target.tagName === 'IMG') {
            const w = window.open('', '_blank');
            w.document.write(`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${e.target.src}" style="max-width:100%;max-height:100vh;object-fit:contain"></body></html>`);
          }
        }}
      />
    </div>
  );
}

/* ── Section combobox ── */
function SectionInput({ value, onChange, allSections }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const suggestions = allSections.filter(s => s && s.toLowerCase().includes(value.toLowerCase()) && s !== value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
        border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)', color: 'var(--text)' }}>
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, opacity: .5 }}>
          <path d="M3 6h18M3 12h18M3 18h18"/>
        </svg>
        <input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Section (optional)…"
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13,
            color: 'var(--text)', width: 180 }}
        />
        {value && (
          <button onMouseDown={e => { e.preventDefault(); onChange(''); }}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
          marginTop: 2, minWidth: 200 }}>
          {suggestions.map(s => (
            <div key={s} onMouseDown={e => { e.preventDefault(); onChange(s); setOpen(false); }}
              style={{ padding: '7px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Wiki Page ── */
export default function WikiPage() {
  const [articles, setArticles]     = useState([]);
  const [selected, setSelected]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [editing, setEditing]       = useState(false);
  const [editTitle, setEditTitle]   = useState('');
  const [editContent, setEditContent] = useState('');
  const [editSection, setEditSection] = useState('');
  const [saving, setSaving]         = useState(false);
  const [search, setSearch]         = useState('');
  const [deleting, setDeleting]     = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const autoSaveTimer = useRef(null);
  const [collapsed, setCollapsed]   = useState(() => {
    try { return JSON.parse(localStorage.getItem('wikiSectionsCollapsed') || '{}'); } catch { return {}; }
  });

  const loadList = () => {
    fetch(`${API}/api/wiki`)
      .then(r => r.json())
      .then(setArticles)
      .catch(() => {});
  };

  useEffect(() => { loadList(); }, []);

  const openArticle = (id) => {
    setLoading(true); setEditing(false);
    fetch(`${API}/api/wiki/${id}`)
      .then(r => r.json())
      .then(a => { setSelected(a); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const startNew = () => {
    setSelected(null); setEditTitle(''); setEditContent(''); setEditSection(''); setEditing(true);
  };

  const startEdit = () => {
    setEditTitle(selected.title);
    setEditContent(selected.content);
    setEditSection(selected.section || '');
    setEditing(true);
  };

  const save = (silent = false) => {
    if (!editTitle.trim()) return;
    if (!silent) setSaving(true);
    const isNew = !selected;
    const url    = isNew ? `${API}/api/wiki` : `${API}/api/wiki/${selected.id}`;
    const method = isNew ? 'POST' : 'PUT';
    fetch(url, { method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: editTitle, content: editContent, section: editSection }) })
      .then(r => r.json())
      .then(a => {
        if (silent) {
          setAutoSaveStatus('saved');
          setTimeout(() => setAutoSaveStatus(''), 2000);
          loadList();
        } else {
          setSaving(false); setEditing(false);
          loadList();
          if (isNew) openArticle(a.id); else openArticle(selected.id);
        }
      })
      .catch(() => { setSaving(false); setAutoSaveStatus(''); });
  };

  useEffect(() => {
    if (!editing || !editTitle.trim() || !selected) return;
    setAutoSaveStatus('saving');
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => save(true), 2000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [editTitle, editContent, editSection]);

  const deleteArticle = () => {
    if (!selected || !window.confirm(`Delete "${selected.title}"?`)) return;
    setDeleting(true);
    fetch(`${API}/api/wiki/${selected.id}`, { method: 'DELETE' })
      .then(() => { setSelected(null); setEditing(false); setDeleting(false); loadList(); })
      .catch(() => setDeleting(false));
  };

  const toggleSection = (name) => {
    const next = { ...collapsed, [name]: !collapsed[name] };
    setCollapsed(next);
    localStorage.setItem('wikiSectionsCollapsed', JSON.stringify(next));
  };

  const allSections = [...new Set(articles.map(a => a.section).filter(Boolean))].sort();

  const filtered = articles.filter(a => !search ||
    a.title.toLowerCase().includes(search.toLowerCase()) ||
    (a.section || '').toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(a => {
    const key = a.section?.trim() || '__none__';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  });
  const sectionKeys = Object.keys(grouped).filter(k => k !== '__none__').sort();
  if (grouped['__none__']) sectionKeys.push('__none__');

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 120px)', minHeight: 500 }}>

      {/* ── Left sidebar ── */}
      <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--surface)' }}>
        <div style={{ padding: '12px 12px 8px' }}>
          <button onClick={startNew}
            style={{ width: '100%', padding: '8px 12px', background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
              fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Article
          </button>
        </div>
        <div style={{ padding: '0 12px 8px' }}>
          <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)',
              color: 'var(--text)', outline: 'none' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '20px 16px', fontSize: 13, color: 'var(--text-2)', textAlign: 'center' }}>
              {articles.length === 0 ? 'No articles yet.\nCreate your first one.' : 'No results.'}
            </div>
          )}

          {sectionKeys.map(key => {
            const isNone = key === '__none__';
            const items  = grouped[key];
            const label  = isNone ? 'General' : key;
            const isCollapsed = collapsed[key];

            return (
              <div key={key}>
                <div {...clickable(() => toggleSection(key))} aria-expanded={!isCollapsed} aria-label={`${label} section`}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px 6px',
                    cursor: 'pointer', userSelect: 'none', marginTop: key === sectionKeys[0] ? 0 : 4,
                    background: 'var(--surface-2)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.96)'}
                  onMouseLeave={e => e.currentTarget.style.filter = ''}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    style={{ flexShrink: 0, transition: 'transform .15s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', color: 'var(--text-2)' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    color: 'var(--text)', flex: 1 }}>{label}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-2)', background: 'var(--border)', borderRadius: 10, padding: '1px 6px' }}>{items.length}</span>
                </div>

                {!isCollapsed && items.map(a => (
                  <div key={a.id} {...clickable(() => openArticle(a.id))} aria-label={a.title}
                    style={{ padding: '9px 16px 9px 22px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                      background: selected?.id === a.id ? 'var(--accent-light)' : '',
                      borderLeft: selected?.id === a.id ? '3px solid var(--accent)' : '3px solid transparent',
                      transition: 'background .1s' }}
                    onMouseEnter={e => { if (selected?.id !== a.id) e.currentTarget.style.background = 'var(--surface-2)'; }}
                    onMouseLeave={e => { if (selected?.id !== a.id) e.currentTarget.style.background = ''; }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{fmtDate(a.updated_at)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Right content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {!selected && !editing && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)' }}>
            <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom: 16, opacity: .4 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Select an article or create a new one</div>
            <div style={{ fontSize: 13 }}>You can paste screenshots directly into the editor</div>
          </div>
        )}

        {editing && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 24px', gap: 10, overflow: 'hidden' }}>
            <input type="text" placeholder="Article title…" value={editTitle} onChange={e => setEditTitle(e.target.value)}
              style={{ fontSize: 18, fontWeight: 700, padding: '8px 0',
                border: 'none', borderBottom: '2px solid var(--border)', outline: 'none',
                background: 'transparent', color: 'var(--text)', width: '100%' }} />
            <SectionInput value={editSection} onChange={setEditSection} allSections={allSections} />
            <RichEditor value={editContent} onChange={setEditContent} />
            <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
              <button onClick={() => save(false)} disabled={saving || !editTitle.trim()}
                style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none',
                  borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  opacity: saving || !editTitle.trim() ? .6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => { setEditing(false); if (!selected) setSelected(null); }}
                style={{ padding: '8px 16px', background: 'var(--surface-2)', border: '1px solid var(--border)',
                  borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              {autoSaveStatus === 'saving' && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Autosaving…</span>}
              {autoSaveStatus === 'saved'  && <span style={{ fontSize: 11, color: 'var(--ok)' }}>✓ Saved</span>}
            </div>
          </div>
        )}

        {selected && !editing && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {selected.section && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase',
                    letterSpacing: '.08em', marginBottom: 4 }}>{selected.section}</div>
                )}
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{selected.title}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                  Created {selected.created_at} · Updated {selected.updated_at}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={startEdit}
                  style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none',
                    borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
                <button onClick={deleteArticle} disabled={deleting}
                  style={{ padding: '6px 12px', background: 'var(--surface-2)', color: 'var(--critical)',
                    border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                  {deleting ? '…' : 'Delete'}
                </button>
              </div>
            </div>
            {loading ? (
              <div className="loading">Loading…</div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                <ArticleView article={selected} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
